<script setup>
import LoadingArea from "@/components/utils/LoadingArea.vue";
import LabelledField from "@/components/utils/LabelledField.vue";
import Pagination from "@/components/utils/Pagination.vue";
import SchemePlanApplicationsTable from "./SchemePlanApplicationsTable.vue";
import SchemePlanApplicationDetails from "./SchemePlanApplicationDetails.vue";
import SchemePlanApplicationResolve from "./SchemePlanApplicationResolve.vue";
import { useFetchClientCreatedSchemePlanApplications, preloadedDraft, useLoadDraft } from "./scheme_plan_application_business_logic";
import { computed, ref, getCurrentInstance } from "vue";
import apiCall from "@/utils/api";
import { routes } from "@/router/routes";

const {
  applicationData,
  searchModel,
  fetchApplications,
} = useFetchClientCreatedSchemePlanApplications();

const { loadDraft } = useLoadDraft();

// Page navigation
const pages = {
  VIEW_TABLE: 0,
  VIEW_APPLICATION: 1,
  RESOLVE_QUERY: 2,
};
const currentPage = ref(pages.VIEW_TABLE);
const applicationToView = ref(null);
const applicationToResolve = ref(null);
const fetchingApplicationDetails = ref(false);
const loadingApplicationId = ref(null);
const loadingContinueId = ref(null);

function onViewApplicationClicked(application) {
  fetchingApplicationDetails.value = true;
  loadingApplicationId.value = application.id;
  apiCall({
    url: `/api/client/scheme-plan-application/${application.id}`,
    method: "GET",
  })
    .then((resp) => {
      applicationToView.value = resp;
      currentPage.value = pages.VIEW_APPLICATION;
    })
    .catch((error) => {
      console.log(error.response);
      applicationToView.value = application;
      currentPage.value = pages.VIEW_APPLICATION;
    })
    .finally(() => {
      fetchingApplicationDetails.value = false;
      loadingApplicationId.value = null;
    });
}

function onBackToTable() {
  currentPage.value = pages.VIEW_TABLE;
  fetchApplications();
}

function onPaginationClicked(newPage) {
  applicationData.setNewPage(newPage);
}

function onResolveQueryClicked(application) {
  applicationToResolve.value = application;
  currentPage.value = pages.RESOLVE_QUERY;
}

function onResolved() {
  currentPage.value = pages.VIEW_TABLE;
  fetchApplications();
}

const instance = getCurrentInstance();
function onPayBillClicked(application) {
  const bill = application.bill;
  instance.proxy.$router.push(
    routes.payment.withQuery({
      billNumbersToPayAsString: bill.bill_number,
      directPayment: true,
    })
  );
}

async function onContinueDraftClicked(application) {
  loadingContinueId.value = application.id;
  try {
    const draft = await loadDraft(application.id);
    preloadedDraft.value = draft;
    instance.proxy.$router.push({
      name: routes.createSchemePlanApplication.name,
      query: { draft: application.id },
    });
  } catch (error) {
    console.error("Failed to load draft application", error);
  } finally {
    loadingContinueId.value = null;
  }
}
</script>

<template>
  <v-container fluid class="pa-0">
    <v-tabs-items v-model="currentPage" touchless>
      <!-- Tab 0: Applications Table -->
      <v-tab-item :value="pages.VIEW_TABLE">
        <v-row>
          <v-col cols="12">
            <div class="text-h6 text-center font-weight-bold primary--text">
              Client Scheme Plan Applications
            </div>
          </v-col>
        </v-row>

        <v-row justify="center">
          <v-col cols="12" md="6">
            <LabelledField label="Search">
              <v-text-field
                class="background text-field"
                outlined
                dense
                v-model="searchModel"
                clearable
                placeholder="Search by application number, PN, LR Number..."
              />
            </LabelledField>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <LoadingArea :loading="applicationData.fetchingData">
              <SchemePlanApplicationsTable
                :applications="applicationData.fetchedData"
                :applicationsNotEmpty="applicationData.fetchedDataNotEmpty"
                :loadingApplicationId="loadingApplicationId"
                :loadingContinueId="loadingContinueId"
                @viewApplicationClicked="onViewApplicationClicked"
                @payBillClicked="onPayBillClicked"
                @resolveQueryClicked="onResolveQueryClicked"
                @continueClicked="onContinueDraftClicked"
              />
            </LoadingArea>
          </v-col>
        </v-row>

        <v-row justify="end">
          <v-col cols="auto">
            <Pagination
              :totalNumberOfItems="applicationData.paginationData.total"
              :itemsPerPage="applicationData.paginationData.perPage"
              :currentPage="applicationData.paginationData.currentPage"
              @paginationClicked="onPaginationClicked"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Tab 1: Application Detail -->
      <v-tab-item :value="pages.VIEW_APPLICATION">
        <v-row>
          <v-col cols="12">
            <SchemePlanApplicationDetails
              v-if="applicationToView"
              :application="applicationToView"
              @goBackClicked="onBackToTable"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Tab 2: Resolve Query -->
      <v-tab-item :value="pages.RESOLVE_QUERY">
        <v-row>
          <v-col cols="12">
            <SchemePlanApplicationResolve
              v-if="applicationToResolve"
              :application="applicationToResolve"
              @goBackClicked="currentPage = pages.VIEW_TABLE"
              @resolved="onResolved"
            />
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>
